import type { App, DirectiveBinding, Ref } from 'vue';

interface DropHandlerOptions {
  domain: string;
  domainId: string;
  path?: string;
  isHovering: Ref<boolean>;
  files: File[] | null;
  onDropStarted?: (files: File[]) => void;
  onDropProgress?: (files: File[]) => void;
  onDropCompleted?: (files: File[]) => void;
  onDropError?: (error: Error) => void;
}

function handleFileDrop(event: DragEvent, options: DropHandlerOptions): void {
  event.preventDefault();
  options.isHovering.value = false;
  if (!event.dataTransfer?.files.length) {
    options.onDropError?.(new Error('No files were dropped'));
    return;
  }

  const files = Array.from(event.dataTransfer.files);
  const validFiles = files.filter(() => {
    // TODO: Handle only valid files
    return true;
  });

  if (validFiles.length === 0) {
    options.onDropError?.(new Error('No valid files were dropped'));
    return;
  }

  options.files = validFiles;
  options.onDropCompleted?.(validFiles);
}

let onDrop: (event: DragEvent) => void;
let onDragOver: (event: DragEvent) => void;
let onDragLeave: (event: DragEvent) => void;

const dropDirective = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    const options: DropHandlerOptions = {
      files: binding.value.files,
      domain: binding.value.domain,
      domainId: binding.value.resourceId,
      path: binding.value?.path,
      isHovering: binding.value.isHovering,
      onDropError: binding.value.onDropError,
      onDropStarted: binding.value.onDropStarted,
      onDropProgress: binding.value.onDropProgress,
      onDropCompleted: binding.value.onDropCompleted,
    };

    onDrop = (event: DragEvent) => {
      options.onDropStarted?.(Array.from(event.dataTransfer?.files || []));
      options.onDropProgress?.(Array.from(event.dataTransfer?.files || []));
      handleFileDrop(event, options);
    };

    onDragOver = (event: DragEvent) => {
      event.preventDefault();
      options.isHovering.value = true;
    };

    onDragLeave = () => {
      options.isHovering.value = false;
    };

    el.addEventListener('dragover', onDragOver);
    el.addEventListener('dragleave', onDragLeave);
    el.addEventListener('drop', onDrop);
  },
  unmounted(el: HTMLElement) {
    el.removeEventListener('dragover', onDragOver);
    el.removeEventListener('dragleave', onDragLeave);
    el.removeEventListener('drop', onDrop);
  },
};

export function installFileDropDirective(app: App): void {
  app.directive('file-drop', dropDirective);
}
